import { format } from "date-fns";
import { useCallback } from "react";
import { type Activity, type Matter, type Task } from "@/features/attorney/components/v2/EditEntryBody";
import { EntryForm } from "@/features/attorney/components/v2/EntryForm";
import type { TimeEntryFormValues } from "@/features/attorney/components/v2/schema";
import type { Abbreviation } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";

type SaveEntry = {
  id: number;
  date: string;
  matter: string | null;
  matter_id: string;
  description: string;
  duration: number;
  task: string;
  activity: string;
};

type InternalAddEntryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (entry: SaveEntry, shouldExport: boolean) => void;
  matters: Matter[];
  tasks: Task[];
  activities: Activity[];
  currentDate: Date;
  abbreviations?: Abbreviation[];
};

function toSaveEntry(values: TimeEntryFormValues): SaveEntry {
  return {
    id: 0,
    date: values.date ? format(values.date, "MM/dd/yyyy") : "",
    matter: values.matter,
    matter_id: values.matter_id,
    description: values.description,
    duration: values.duration,
    task: values.task,
    activity: values.activity,
  };
}

export function InternalAddEntryDialog({
  open,
  onOpenChange,
  onSave,
  matters,
  tasks,
  activities,
  currentDate,
  abbreviations,
}: InternalAddEntryDialogProps) {
  const submit = useCallback(
    (values: TimeEntryFormValues, shouldExport: boolean) => {
      onSave(toSaveEntry(values), shouldExport);
      onOpenChange(false);
    },
    [onOpenChange, onSave],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto p-6 sm:max-w-3xl">
        <DialogTitle className="mb-4">Add entry</DialogTitle>
        <EntryForm
          matters={matters}
          tasks={tasks}
          activities={activities}
          currentDate={currentDate}
          defaultValues={{ date: currentDate, duration: 0 }}
          onSubmit={(values) => submit(values, false)}
          onExport={(values) => submit(values, true)}
          onCancel={() => onOpenChange(false)}
          submitLabel="Add entry"
          abbreviations={abbreviations}
        />
      </DialogContent>
    </Dialog>
  );
}
